/**
 * Flow → Products (PLAN_FLOW_PRODUK.md Fasa 1, 30 Sept 2026).
 *
 * Admin tambah / rename / tukar harga, tempoh, had credit. Satu produk Type 1
 * boleh ditanda "Sold on Billplz" — server Billplz ambil harga + tempoh dari situ
 * (functions/flowLangganan.js → produkFlowJualan). Tiada produk ditanda = RM100 /
 * 30 hari seperti sebelum Fasa 1.
 * Tiada butang padam — produk lama dirujuk oleh resit. "Active" off = berhenti jual.
 */
import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Badge, Btn, IconBtn } from '../design/ui';
import { simpanProduk, semakProduk, rmDariSen, senDariTeks } from '../services/flowProdukService';

const KOSONG = { id: null, name: '', jenis: 1, harga: '', days: '30', had: '4500', onBillplz: false, active: true };

export default function FlowProductsScreen({ produk, ralatMuat, onSaved, subsCount }) {
    const [borang, setBorang] = useState(null);
    const [sedang, setSedang] = useState(false);
    const [ralat, setRalat] = useState('');

    const buka = (p) => {
        setRalat('');
        setBorang(p ? {
            id: p.id, name: p.name, jenis: p.jenis, harga: String(p.priceSen / 100), days: String(p.days),
            had: p.hadKredit ? String(p.hadKredit) : '4500', onBillplz: !!p.onBillplz, active: p.active !== false, order: p.order,
        } : { ...KOSONG, onBillplz: produk.length === 0 });
    };
    // Produk pertama: borang diisi nilai yang sedang dicaj (RM100 / 30 hari), admin semak + Save.
    const bukaPermulaan = () => { setRalat(''); setBorang({ ...KOSONG, name: 'Flow Ultra — Lite LP', harga: '100', onBillplz: true }); };

    const ubah = (k, v) => setBorang((b) => ({ ...b, [k]: v, ...(k === 'jenis' && v === 2 ? { onBillplz: false } : {}) }));

    const nilai = borang && {
        id: borang.id, name: borang.name, jenis: borang.jenis,
        priceSen: senDariTeks(borang.harga), days: Math.round(Number(borang.days)),
        hadKredit: Math.round(Number(borang.had)), onBillplz: borang.onBillplz, active: borang.active, order: borang.order,
    };
    const asal = borang?.id ? produk.find((p) => p.id === borang.id) : null;
    const hargaBerubah = asal && asal.onBillplz && nilai && (nilai.priceSen !== asal.priceSen || nilai.days !== asal.days);

    const simpan = async () => {
        const r = semakProduk(nilai);
        if (r) { setRalat(r); return; }
        setSedang(true); setRalat('');
        try {
            await simpanProduk(nilai, produk);
            setBorang(null);
            onSaved?.(nilai.id ? 'Product saved.' : 'Product added.');
        } catch (e) {
            setRalat(e?.code === 'permission-denied'
                ? 'Not saved — the database rules for products are not deployed yet.'
                : `Not saved: ${e?.message || 'unknown error'}`);
        } finally { setSedang(false); }
    };

    const dijual = produk.find((p) => p.onBillplz && p.active !== false);

    return (
        <>
            <div className="s-sec">
                <div>
                    <h2>Products</h2>
                    <p>What you sell. Price and duration here are what Billplz charges.</p>
                </div>
                <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
                    <Btn size="sm" icon={<Plus size={14} />} onClick={() => buka(null)}>Add product</Btn>
                </div>
            </div>

            {ralatMuat && (
                <div className="s-sample" style={{ background: 'var(--s-stop-soft)', color: 'var(--s-stop)' }}>
                    <b>Could not read products.</b> {ralatMuat}
                </div>
            )}

            <div className="s-sample" style={{ marginBottom: 14 }}>
                {dijual
                    ? <>Billplz sells <b>{dijual.name}</b> — {rmDariSen(dijual.priceSen)} for {dijual.days} days.</>
                    : <>No product is set for Billplz, so Billplz still charges <b>RM100 for 30 days</b>.</>}
                {' '}One Type 1 product can be sold on Billplz for now. Type 2 on Billplz comes in a later phase.
            </div>

            {produk.length === 0 && !ralatMuat ? (
                <div className="fu-tblwrap" style={{ padding: '28px 20px', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 6px', fontWeight: 500 }}>Add your first product</p>
                    <p className="s-dim" style={{ margin: '0 0 14px' }}>
                        Start with what Billplz charges today (RM100 for 30 days), then add Type 2.
                    </p>
                    <Btn size="sm" onClick={bukaPermulaan}>Add Type 1 product</Btn>
                </div>
            ) : (
                <div className="fu-tblwrap">
                    <table className="fu-tbl">
                        <thead>
                            <tr><th>Product</th><th>Type</th><th>Price</th><th>Duration</th><th>Credit limit</th><th>Subscriptions</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                            {produk.map((p) => (
                                <tr key={p.id} onClick={() => buka(p)}>
                                    <td><span className="fu-tbl__who">{p.name}</span></td>
                                    <td>Type {p.jenis}</td>
                                    <td className="s-num">{rmDariSen(p.priceSen)}</td>
                                    <td className="s-num">{p.days} days</td>
                                    <td className="s-num">{p.jenis === 2 && p.hadKredit ? p.hadKredit.toLocaleString('en-MY') : '—'}</td>
                                    <td className="s-num">{subsCount(p.id)}</td>
                                    <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {p.active === false ? <Badge tone="neutral">Inactive</Badge> : <Badge tone="ok">Active</Badge>}
                                        {p.onBillplz && <Badge tone="brand">On Billplz</Badge>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {borang && (
                <div className="fu-modal-bg" onClick={() => !sedang && setBorang(null)}>
                    <div className="fu-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="fu-modal__h">
                            <div>
                                <p className="s-cardname">{borang.id ? 'Edit product' : 'Add product'}</p>
                                <p className="s-dim">{borang.id ? 'Changes apply to the next payment. Past receipts keep their price.' : 'A product customers can buy or you can add manually.'}</p>
                            </div>
                            <IconBtn label="Close" icon={<X size={15} />} onClick={() => !sedang && setBorang(null)} />
                        </div>

                        <div className="fu-modal__b">
                            <div>
                                <p className="s-kv__lab">Product name</p>
                                <input className="s-inp" value={borang.name} maxLength={80} placeholder="Flow Ultra — Lite LP"
                                    onChange={(e) => ubah('name', e.target.value)} />
                            </div>
                            <div>
                                <p className="s-kv__lab">Type</p>
                                <select className="s-inp" value={borang.jenis} disabled={!!borang.id}
                                    onChange={(e) => ubah('jenis', Number(e.target.value))}>
                                    <option value={1}>Type 1 — Lite LP (shared family Gmail, Open Flow)</option>
                                    <option value={2}>Type 2 — own Gmail with credits</option>
                                </select>
                                {borang.id && <p className="s-kv__hint">Type cannot change after a product is created — each type uses a different Gmail.</p>}
                            </div>
                            <div className="s-kv">
                                <div>
                                    <p className="s-kv__lab">Price (RM)</p>
                                    <input className="s-inp" value={borang.harga} inputMode="decimal" placeholder="100"
                                        onChange={(e) => ubah('harga', e.target.value)} />
                                </div>
                                <div>
                                    <p className="s-kv__lab">Duration (days)</p>
                                    <input className="s-inp" value={borang.days} inputMode="numeric"
                                        onChange={(e) => ubah('days', e.target.value)} />
                                </div>
                                {borang.jenis === 2 && (
                                    <div>
                                        <p className="s-kv__lab">Credit limit</p>
                                        <input className="s-inp" value={borang.had} inputMode="numeric"
                                            onChange={(e) => ubah('had', e.target.value)} />
                                    </div>
                                )}
                            </div>

                            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
                                <input type="checkbox" checked={borang.active} onChange={(e) => ubah('active', e.target.checked)}
                                    style={{ marginTop: 3 }} />
                                <span>
                                    <span style={{ display: 'block' }}>Active</span>
                                    <span className="s-dim" style={{ fontSize: 12.5 }}>Turn off to stop selling it. Existing subscriptions are not affected.</span>
                                </span>
                            </label>
                            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: borang.jenis === 1 ? 'pointer' : 'not-allowed', opacity: borang.jenis === 1 ? 1 : 0.55 }}>
                                <input type="checkbox" checked={borang.onBillplz} disabled={borang.jenis !== 1}
                                    onChange={(e) => ubah('onBillplz', e.target.checked)} style={{ marginTop: 3 }} />
                                <span>
                                    <span style={{ display: 'block' }}>Sold on Billplz</span>
                                    <span className="s-dim" style={{ fontSize: 12.5 }}>
                                        {borang.jenis === 1
                                            ? 'Customers paying on the Flow page are charged this price and get these days. Only one product at a time — ticking this unticks the others.'
                                            : 'Type 2 on Billplz comes in a later phase. Add Type 2 customers manually for now.'}
                                    </span>
                                </span>
                            </label>

                            {hargaBerubah && (
                                <p className="s-kv__hint" style={{ color: 'var(--s-warn)' }}>
                                    This product is on Billplz. The next customer who pays will be charged the new price and duration.
                                </p>
                            )}
                            {ralat && <p style={{ color: 'var(--s-stop)', fontSize: 13, margin: 0 }}>{ralat}</p>}
                        </div>

                        <div className="fu-modal__f">
                            <Btn variant="quiet" onClick={() => setBorang(null)} disabled={sedang}>Cancel</Btn>
                            <Btn onClick={simpan} disabled={sedang}>{sedang ? 'Saving…' : borang.id ? 'Save' : 'Add product'}</Btn>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
